<template>
  <div
    v-if="isEmpty"
    class="px-4 py-3 flex-1 flex items-center border-b border-gray-100 last:border-b-0"
  >
    <!-- Empty day for consistent heights -->
  </div>

  <div
    v-else
    class="px-4 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-center border-b border-gray-100 last:border-b-0"
  >
    <div class="grid grid-cols-4 gap-4 items-center w-full">
      <div class="text-sm font-medium text-gray-900">
        {{ day.toLocaleString('en-us', { day: '2-digit', weekday: 'short'}) }}
      </div>

      <template v-if="typingQuantity">
        <div class="flex items-center justify-center">
          <form
            action=""
            @submit.prevent=""
          >
            <AppInput
              ref="quantityInput"
              v-model="quantity"
              name="quantity"
              type="number"
              min="0"
              data-test="quantity-input"
              class="form-input w-20 h-8 px-2 py-1 text-sm"
              @blur="saveQuantity"
              @keyup.enter="saveQuantity"
              @keyup.esc="typingQuantity = false"
            />
          </form>
        </div>
      </template>

      <div
        v-else-if="projectsStore.project?.unarchived"
        class="text-right text-sm font-medium text-gray-900"
        :class="{
          'bg-red-50 border border-red-200 rounded px-2 py-1 h-8': (!$props.dailyQuantity && day.getTime() < new Date().getTime() && $props.cost),
          'cursor-pointer border border-gray-300 rounded px-2 py-1 h-8 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-150': ($props.showCaptured || ($props.cost && !$props.dailyQuantity))
        }"
        @click="toggleTypingQuantity"
      >
        {{ $props.dailyQuantity ?
          australianNumber(
            showCaptured ?
              $props.dailyQuantity.quantity :
              $props.dailyQuantity.surveyedQuantity
          ) : ''
        }}
      </div>

      <div
        v-else
        class="text-right text-sm font-medium text-gray-900"
      >
        {{ $props.dailyQuantity ?
          australianNumber(
            showCaptured ?
              $props.dailyQuantity.quantity :
              $props.dailyQuantity.surveyedQuantity
          ) : ''
        }}
      </div>

      <div class="text-right text-sm text-gray-900">
        {{ $props.cost ? australianCurrency($props.cost) : '' }}
      </div>

      <div
        v-if="$props.cost &&
          ((!showCaptured && $props.dailyQuantity?.surveyedQuantity) ||
            (showCaptured && $props.dailyQuantity?.quantity))"
        class="text-right text-sm text-gray-600"
      >
        {{
          showCaptured ?
            australianCurrency($props.cost/$props.dailyQuantity.quantity) :
            australianCurrency($props.cost/$props.dailyQuantity.surveyedQuantity)
        }}/{{ wbsCode?.unit }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, dateAsYMD, falsey } from '@/helpers';
import DailyQuantity from '@/models/DailyQuantity';
import WbsCode from '@/models/WbsCode';
import { useDailyQuantitiesStore } from '@/store/dailyQuantities';
import { useProjectsStore } from '@/store/projects';
import Swal from 'sweetalert2';
import { nextTick, ref } from 'vue';

const props = defineProps<{
  dailyQuantity?: DailyQuantity;
  cost?: number;
  day: Date;
  showCaptured: boolean;
  wbsCode: WbsCode;
  isEmpty?: boolean;
}>();

const typingQuantity = ref(false);
const quantity = ref(0);
const quantityInput = ref();

const toggleTypingQuantity = () => {
  const quantityHasBias = typeof props.dailyQuantity?.bias === 'number';

  if((props.showCaptured && !quantityHasBias) || (props.cost && !props.dailyQuantity)) {
    quantity.value = props.dailyQuantity?.quantity || 0;
    typingQuantity.value = !typingQuantity.value;

    if(typingQuantity.value) {
      nextTick(() => {
        quantityInput.value?.focusInput();
      });
    }
  }
};

const dailyQuantitiesStore = useDailyQuantitiesStore();
const projectsStore = useProjectsStore();
const storeQuantityAction = useStoreApiAction(dailyQuantitiesStore.createQuantity);
const updateQuantityAction = useStoreApiAction(dailyQuantitiesStore.updateQuantity);
const deleteQuantityAction = useStoreApiAction(dailyQuantitiesStore.deleteQuantity);

const saveQuantity = () => {
  if(props.dailyQuantity) {
    if(quantity.value) {
      updateQuantityAction.request(props.dailyQuantity.id, { quantity: quantity.value }).then(() => {
        typingQuantity.value = false;
      }).catch(async (error) => {
        if(error?.status === 422) {
          await Swal.fire({
            icon: 'error',
            text: error.data.message,
          });
        } else {
          await Swal.fire({
            icon: 'error',
            text: 'Failed to Update.',
          });
        }

        quantityInput.value?.focusInput();
      });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Remove Quantity?',
        text: 'Are you sure you want to delete this captured quantity?',
        showCancelButton: true,
        showConfirmButton: true,
      }).then((result) => {
        if(result.isConfirmed) {
          deleteQuantityAction.request(props.dailyQuantity.id).then(() => {
            typingQuantity.value = false;

            Swal.fire({
              icon: 'success',
              text: 'Quantity Deleted.',
            });
          }).catch(() => {
            Swal.fire({
              icon: 'error',
              text: 'Failed to Delete.',
            });
          });
        } else {
          typingQuantity.value = false;
        }
      });
    }
  } else {
    if(!falsey(quantity.value)) {
      storeQuantityAction
        .request(projectsStore.project?.id, {
          day: dateAsYMD(props.day),
          quantity: quantity.value,
          wbs_code_id: props.wbsCode.id,
        })
        .then((_quantity) => {
          typingQuantity.value = false;
        })
        .catch(async (error) => {
          if(error?.status === 422) {
            await Swal.fire({
              icon: 'error',
              text: error.data.message,
            });
          } else {
            await Swal.fire({
              icon: 'error',
              text: 'Failed to Save.',
            });
          }

          quantityInput.value?.focusInput();
        });
    } else {
      typingQuantity.value = false;
    }
  }
};
</script>

<style scoped></style>
